import React from 'react';
import { useSelector } from 'react-redux';

// Configs, contexts and utils
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Shared components
import { LayoutSingleColumn, NamedLink, Page } from '../../components';

// Containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Same directory
import {
  CONTACT_EMAIL,
  GUARANTEES,
  INSTALLMENT_EXAMPLE,
  PARTNERS,
  PAYMENT_METHODS,
  REFUNDS,
  SECURITY_POINTS,
} from './paymentContent';

import css from './PaymentPage.module.css';

const ICON_PATHS = {
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l8 3v6c0 4.5-3.4 8.2-8 9-4.6-.8-8-4.5-8-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  phone: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M10 12l1.5 1.5L14.5 10" />
    </>
  ),
  refund: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h11a5 5 0 010 10h-3" />
    </>
  ),
};

const Icon = ({ name, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICON_PATHS[name]}
  </svg>
);

// Simple marks of the payment methods, drawn in CSS (the official logo files can replace them)
const MethodMark = ({ id }) => {
  switch (id) {
    case 'bancontact':
      return (
        <span className={`${css.mark} ${css.markBancontact}`}>
          <b>Ban</b>contact
        </span>
      );
    case 'visa':
      return <span className={`${css.mark} ${css.markVisa}`}>VISA</span>;
    case 'mastercard':
    case 'maestro':
      return (
        <span className={`${css.mark} ${id === 'maestro' ? css.markMaestro : css.markMastercard}`}>
          <i />
          <i />
        </span>
      );
    case 'revolut':
      return <span className={`${css.mark} ${css.markRevolut}`}>Revolut</span>;
    case 'link':
      return <span className={`${css.mark} ${css.markLink}`}>link</span>;
    case 'scalapay':
      return <span className={`${css.mark} ${css.markScalapay}`}>scalapay</span>;
    case 'stripe':
      return <span className={`${css.mark} ${css.markStripe}`}>stripe</span>;
    default:
      return null;
  }
};

// 300 → "300 €", 75 → "75 €"
const euros = amount => `${amount} €`;

/**
 * Secure payment page (/paiement), linked from the footer: reassures learners about payments
 * with Stripe and Scalapay, the accepted payment methods and refunds. There is no payment here.
 * Replaces the Console page /p/paiement.
 *
 * @component
 * @returns {JSX.Element}
 */
const PaymentPage = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const { total, counts } = INSTALLMENT_EXAMPLE;

  return (
    <Page
      title={intl.formatMessage(
        { id: 'PaymentPage.metaTitle' },
        { marketplaceName: config.marketplaceName }
      )}
      description={intl.formatMessage({ id: 'PaymentPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.hero}>
            <div className={css.wrap}>
              <p className={css.kicker}>Paiement 100 % sécurisé</p>
              <h1 className={css.title}>Réservez votre formation en toute confiance</h1>
              <p className={css.lead}>
                Tous les paiements sur Formabel passent par <strong>Stripe</strong>, la référence
                mondiale du paiement en ligne, et notre partenaire officiel{' '}
                <strong>Scalapay</strong> pour payer en plusieurs fois.
              </p>
              <div className={css.marks}>
                {PAYMENT_METHODS.filter(m => m.id !== 'stripe').map(m => (
                  <MethodMark key={m.id} id={m.id} />
                ))}
              </div>
              <ul className={css.guarantees}>
                {GUARANTEES.map(g => (
                  <li key={g.title} className={css.guarantee}>
                    <span className={css.guaranteeIcon}>
                      <Icon name={g.icon} />
                    </span>
                    <span>
                      <strong>{g.title}</strong>
                      <small>{g.text}</small>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </header>

          <div className={css.wrap}>
            <section className={css.section}>
              <h2 className={css.sectionTitle}>Nos partenaires de paiement officiels</h2>
              <p className={css.intro}>
                Des entreprises reconnues, qui protègent chaque euro que vous payez.
              </p>
              <div className={css.partners}>
                {PARTNERS.map(p => (
                  <div key={p.id} className={css.partner}>
                    <div className={css.partnerHead}>
                      <span
                        className={`${css.partnerLogo} ${
                          p.id === 'stripe' ? css.partnerStripe : css.partnerScalapay
                        }`}
                      >
                        {p.id}
                      </span>
                      <span className={css.tag}>{p.tag}</span>
                    </div>
                    <p className={css.partnerText}>{p.text}</p>
                    <ul className={css.checkList}>
                      {p.points.map(point => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Moyens de paiement acceptés</h2>
              <p className={css.intro}>Choisissez celui qui vous convient au moment de réserver.</p>
              <ul className={css.methods}>
                {PAYMENT_METHODS.map(m => (
                  <li key={m.id} className={css.method}>
                    <MethodMark id={m.id} />
                    <strong>{m.name}</strong>
                    <small>{m.text}</small>
                  </li>
                ))}
              </ul>
            </section>

            <section className={css.section}>
              <div className={css.split}>
                <div>
                  <p className={css.badge}>Sans intérêts</p>
                  <h2 className={css.splitTitle}>Payez en 3 ou 4 fois avec Scalapay</h2>
                  <p className={css.splitText}>
                    La première échéance est prélevée le jour de la réservation, les suivantes
                    chaque mois. Disponible selon votre éligibilité ; des frais de service Scalapay
                    peuvent s'appliquer.
                  </p>
                </div>
                <div className={css.example}>
                  <p className={css.exampleLabel}>Exemple pour une formation à {euros(total)}</p>
                  {counts.map(count => (
                    <div key={count} className={css.plan}>
                      <p className={css.planTitle}>
                        En {count} fois : <strong>{euros(total / count)}</strong> par mois
                      </p>
                      <div className={css.bars}>
                        {Array.from({ length: count }, (_, i) => (
                          <span key={i} className={i === 0 ? css.barToday : css.bar}>
                            {i === 0 ? "Aujourd'hui" : `Mois ${i + 1}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className={css.section}>
              <div className={css.security}>
                <div>
                  <h2 className={css.sectionTitle}>Vos données bancaires restent protégées</h2>
                  <ul className={css.securityList}>
                    {SECURITY_POINTS.map(p => (
                      <li key={p.title}>
                        <strong>{p.title}</strong>
                        <span>{p.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={css.card} aria-hidden="true">
                  <div className={css.cardTop}>
                    <span className={css.cardChip} />
                    <span className={css.cardLock}>
                      <Icon name="lock" size={13} /> Sécurisé par Stripe
                    </span>
                  </div>
                  <p className={css.cardNumber}>•••• •••• •••• 4242</p>
                  <p className={css.cardNote}>Formabel ne voit que les 4 derniers chiffres</p>
                </div>
              </div>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionTitle}>Annulation et remboursement</h2>
              <p className={css.intro}>
                Le remboursement est fait sur le moyen de paiement utilisé, en général sous 5 à 10
                jours.
              </p>
              <ul className={css.refunds}>
                {REFUNDS.map(r => (
                  <li key={r.when} className={r.full ? css.refundFull : css.refundHalf}>
                    <span>{r.when}</span>
                    <strong>{r.refund}</strong>
                  </li>
                ))}
                <li className={css.refundFull}>
                  <span>La formation est annulée par le formateur</span>
                  <strong>Remboursé à 100 %</strong>
                </li>
              </ul>
              <NamedLink name="FAQPage" className={css.more}>
                Questions sur le paiement : voir la FAQ →
              </NamedLink>
            </section>

            <section className={css.contact}>
              <div className={css.contactBox}>
                <div>
                  <h2 className={css.contactTitle}>Une question sur un paiement ?</h2>
                  <p className={css.contactText}>
                    Un paiement refusé, un remboursement, une facture ? Notre équipe vous répond
                    personnellement.
                  </p>
                </div>
                <a href={`mailto:${CONTACT_EMAIL}`} className={css.contactButton}>
                  {CONTACT_EMAIL}
                </a>
              </div>
            </section>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default PaymentPage;
